import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Button } from '@savvy-kit/components/button';
import { Collapsible, CollapsibleContent } from '@savvy-kit/components/collapsible';
import { Separator } from '@savvy-kit/components/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { ChevronDown, ChevronUp } from 'lucide-react';
import PropTypes from 'prop-types';

type DashboardSidebarItemProps = {
  active?: boolean;
  children?: ReactNode;
  readonly chip?: ReactNode;
  depth: number;
  icon?: ReactNode;
  info?: ReactNode;
  readonly disabled?: boolean;
  open?: boolean;
  path?: string;
  title: string;
} & ComponentProps<'div'>;

function dislabedItemTooltipTitle(path?: string) {
  if (path === '/portfolio' || path?.startsWith('/savvy-portfolio')) {
    return 'Your portfolio will be available for you to view after your accounts are funded.';
  }
  if (path === '/goals') {
    return 'You can track your financial goals in Savvy. Reach out to your advisor to begin.';
  }
  if (path === '/tasks') {
    return 'Tasks created by your advisor will be available for you to view after your accounts are funded.';
  }
  if (path === '/financial-plan') {
    return 'This will be available once your advisor uploads your financial plan.';
  }
  return 'This will be available once you are fully onboarded.';
}

export function DashboardSidebarItem({
  active,
  children,
  chip,
  depth,
  icon,
  disabled,
  info,
  open: openProp,
  path,
  title,
  ...other
}: DashboardSidebarItemProps) {
  const [open, setOpen] = useState<boolean>(openProp || false);

  useEffect(() => {
    setOpen(!!openProp);
  }, [openProp]);

  const handleToggle = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  const sharedButtonStyle: React.CSSProperties = {
    marginLeft: `${(2 + 3 * depth) * 8}px`,
  };

  const wrappedIcon = icon ? <span className="flex items-center justify-center w-5 shrink-0">{icon}</span> : undefined;

  // Branch
  if (children && !disabled) {
    return (
      <>
        <div className="flex w-full items-center" {...other}>
          <Button
            variant="ghost"
            onClick={handleToggle}
            disabled={disabled}
            icon={wrappedIcon}
            iconPosition="left"
            size="sm"
            style={sharedButtonStyle}
            className="pl-3 pr-6 justify-start text-left w-full text-foreground"
          >
            <div className="grow-0">{title}</div>
            {info}
            <span className="ml-auto flex items-center">
              {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            </span>
          </Button>
        </div>
        <Collapsible open={open} className="mt-1 pr-6">
          <CollapsibleContent>{children}</CollapsibleContent>
        </Collapsible>
      </>
    );
  }

  const leafButton = (
    <Button
      variant="ghost"
      disabled={disabled}
      icon={wrappedIcon}
      iconPosition="left"
      size="sm"
      aria-current={active && path !== 'divider' ? 'page' : undefined}
      style={sharedButtonStyle}
      className={`pl-3 pr-6 justify-start text-left w-full text-foreground${active ? ' font-bold bg-sidebar-accent' : ''}${path === 'divider' ? ' pointer-events-none' : ''}`}
    >
      <div className="grow">{path === 'divider' ? <Separator className="bg-border-light" /> : title}</div>
      {info}
      {chip && <span className="mr-3 flex items-center">{chip}</span>}
    </Button>
  );

  // Leaf
  return (
    // w-full is load-bearing: the Link below sets `width: inherit`, which resolves against this
    // element's computed width. MUI ListItem declared `width: 100%`; without it here the link —
    // and the divider Separator inside it — collapses to content width.
    <div className="mb-0.5 flex w-full items-center pr-4">
      {!disabled && (
        <Link
          // eslint-disable-next-line no-script-url, @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
          href={path === 'divider' ? 'javascript:void(0)' : path!}
          className="w-[inherit] no-underline"
        >
          {leafButton}
        </Link>
      )}
      {disabled && (
        <Tooltip>
          <TooltipTrigger render={<span tabIndex={0}>{leafButton}</span>} />
          <TooltipContent>{dislabedItemTooltipTitle(path)}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

DashboardSidebarItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number.isRequired,
  icon: PropTypes.node,
  info: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired,
};

DashboardSidebarItem.defaultProps = {
  active: false,
  open: false,
};
